/**
 * CHANGELOG
 * =========
 * [CO-1] Fixed "y is not defined" errors when using brush to select scatter plot objects
 *            - Changed getColor(y) to getColor(d.y) in data point stroke color (lines 263, 275)
 *            - Changed getColor(y) to getColor(d.y) in brushed points fill color (lines 293, 305)
 *            - Changed getColor(y) to getColor(hoverPoint.y) in hover point fill color (lines 332, 342)
 *            All fixes use dataset value (0 or 1) instead of pixel coordinate or undefined variable
 * [CO-2] Added X and Y axes to scatter plot
 *            - Added d3-axis dependency for axis rendering
 *            - Increased margins (40px bottom, 40px left) to accommodate axis labels
 *            - Created axis generators using axisBottom and axisLeft
 *            - Rendered axes in SVG with proper positioning
 *            - Increased plot height from 50px to 75px in App.js to provide better vertical spacing
 * [CO-3] Changed Y-axis tick marks from automatically generated to actual data values
 *            - Replaced automatically generated tick marks (from CO-2) with tick marks at actual y values from data
 *            - Extracted unique y values from dataset
 *            - Applied tickValues to y-axis to show only data point y coordinates
 * [CO-4] Added grid lines aligned with axis tick marks
 *            - Added vertical grid lines at each x-axis tick position
 *            - Added horizontal grid lines at each y-axis tick position
 *            - Grid lines rendered with light gray dashed style before axes for proper layering
 * [CO-5] Changed point shapes for y=1 data points from circles to diamonds
 *            - Added getDiamondPath helper function to generate diamond SVG paths
 *            - Updated data point rendering to use diamonds for y=1 (green) points, circles for y=0
 *            - Updated brushed points rendering to maintain shape distinction
 *            - Updated hover point rendering to show appropriate shape based on y value
 * [CO-6] Added faux mean lines at x=0 (green) and x=0.1 (yellow)
 *            - Added vertical dashed mean lines matching dataset colors
 *            - Green mean line (y=1 dataset) at x=0 using colors[0]
 *            - Yellow mean line (y=0 dataset) at x=0.1 using colors[1]
 *            - Lines rendered with dashed stroke style matching grid lines
 */

import { extent } from 'd3-array';
// [CO-2] Added d3-axis imports for axis rendering
import { axisBottom, axisLeft } from 'd3-axis';
import { scaleLinear } from 'd3-scale';
// [CO-2] Added d3-selection import for select function to render axes
import { select } from 'd3-selection';
import * as React from 'react';
import useBrush from './useBrush';
import { useHover } from './useHover';

import { useDataStore, useVizStore } from '@/store';
import { VoteType } from '@/types';

type DataPoint = {
  x: number;
  y: number;
  districtId: string;
  id: string;
};

function arrayEquals(arr1: string[], arr2: string[]): boolean {
  //boilerplate to help compare hoverDistricts
  if (arr1.length !== arr2.length) {
    return false;
  }
  const isEqual = arr1.every((value, index) => value === arr2[index]);
  return isEqual;
}

export default function ScatterplotWithBrush() {
  const width = 215;
  const height = 540;
  const pointRadius = 8;
  const districts = useDataStore((s) => s.districts);
  const metricKey = useDataStore((s) => s.metricKey);
  const metricAggregates = useDataStore((s) => s.metricAggregates);
  const findInMetricIndex = useDataStore((s) => s.findInMetricIndex);
  const setHoverFromPlot = useVizStore((s) => s.setHoverFromPlot);
  const hoveredDistricts = useVizStore((s) => s.hoveredDistricts);
  const hoverSource = useVizStore((s) => s.hoverSource);
  const voteSupportColor = useVizStore((s) => s.voteSupportColor);
  const voteAgainstColor = useVizStore((s) => s.voteAgainstColor);
  const voteNoDataColor = useVizStore((s) => s.voteNoDataColor);
  const orientation = 'vertical';
  const colors: [string, string] = [voteSupportColor ?? '#000000', voteAgainstColor ?? '#000000'];

  // Get metric title from index
  const metricTitle = React.useMemo(() => {
    if (!metricKey) return 'Metric';
    const metricEntry = findInMetricIndex(metricKey);
    return metricEntry?.title ?? 'Metric';
  }, [metricKey, findInMetricIndex]);

  // Transform districts data into chart format
  const { combined_all } = React.useMemo(() => {
    const allDistricts = Object.values(districts);
    console.log('All districts data for plot: ', allDistricts);
    console.log('parse test: ', allDistricts[0]?.districtId?.substring(4).toUpperCase());

    const isVertical = orientation === 'vertical';

    const combined_all = allDistricts
      .filter(
        (d) =>
          (d.voteType === VoteType.NAY || d.voteType === VoteType.YEA) &&
          typeof d.metricValue === 'number' &&
          !Number.isNaN(d.metricValue) &&
          d.districtId &&
          d.districtId.length >= 4,
      )
      .map((d) => ({
        x: isVertical ? (d.voteType === VoteType.NAY ? 0 : 1) : d.metricValue!,
        y: isVertical ? d.metricValue! : d.voteType === VoteType.NAY ? 0 : 1,
        districtId: d.districtId!, // Raw district ID for map syncing
        id: d.districtId!.substring(4).toUpperCase(), // Formatted tooltip for display
      }));

    return { combined_all };
  }, [districts, orientation]);

  /**
   * Track which district is hovered from the map.
   *
   * Returns the district ID that is currently being hovered on the map, or null if none.
   * The map only allows hovering over a single district at a time, so this returns:
   * - null when hoverSource is not 'map' or when no district is hovered
   * - The hovered district ID (string) when hoverSource === 'map' and hoveredDistricts has values
   *
   */
  /*const mapHoveredDistrict = React.useMemo(() => {
    if (hoverSource === 'map' && hoveredDistricts.length > 0) {
      const districtId = hoveredDistricts[0];
      //console.log('mapHoveredDistrict:', districtId);
      return districtId;
    }
    //console.log('mapHoveredDistrict: null');
    return null;
  }, [hoverSource, hoveredDistricts]);*/

  // Get mean values from aggregates
  const mean_yea = metricAggregates?.voteSupportMetricAvg ?? null;
  const mean_nay = metricAggregates?.voteAgainstMetricAvg ?? null;
  const mean_all = metricAggregates?.metricAvg ?? null;

  // [CO-2] Increased margins (40px bottom, 40px left) to accommodate axis labels
  const margin = { top: 10, right: 65, bottom: 40, left: 75 };
  const innerWidth = width - margin.left - margin.right;
  const innerHeight = height - margin.top - margin.bottom;

  const data = combined_all;

  function getColor(dataset: number): string {
    if (dataset == 1) {
      return colors[0];
    } else {
      return colors[1];
    }
  }

  // [CO-5] Helper function to create diamond path
  function getDiamondPath(radius: number): string {
    return `M 0,-${radius} L ${radius},0 L 0,${radius} L -${radius},0 Z`;
  }

  const { xExtent, yExtent, yScale, xScale } = React.useMemo(() => {
    const xExtent = extent(data, (d) => d.x) ?? [0, 1];
    const yExtent = extent(data, (d) => d.y) ?? [0, 1];

    const xScale = scaleLinear()
      .domain(xExtent as [number, number])
      .range([10, innerWidth - 20]);
    const yScale = scaleLinear()
      .domain(yExtent as [number, number])
      .range([innerHeight - 10, 10]);

    return { xExtent, yExtent, yScale, xScale };
  }, [data, innerWidth, innerHeight]);

  // [CO-3] Get unique y values from data for y-axis tick marks
  const xValues = React.useMemo<number[]>(() => {
    return [...new Set(data.map((d) => d.x))];
  }, [data]);

  // [CO-4] Extract actual tick values from rendered x-axis to ensure grid lines match exactly
  const [yTickValues, setYTickValues] = React.useState<number[]>([]);

  const xAxisRef = React.useRef<SVGGElement>(null);
  const yAxisRef = React.useRef<SVGGElement>(null);

  // xAxis: bottom axis using xScale
  const xAxis = React.useMemo(() => {
    return axisBottom(xScale)
      .tickValues(xValues)
      .tickFormat((v) => (v === 1 ? 'Yea' : 'Nay'));
  }, [xScale, xValues]);

  const yAxis = React.useMemo(() => {
    return axisLeft(yScale);
  }, [yScale]);

  React.useEffect(() => {
    if (xAxisRef.current) {
      select(xAxisRef.current).call(xAxis);
    }
  }, [xAxis]);

  // Render y (left) axis and extract tick positions for horizontal grid lines
  React.useEffect(() => {
    if (yAxisRef.current) {
      select(yAxisRef.current).call(yAxis);
      const ticks = select(yAxisRef.current).selectAll('.tick').nodes() as SVGElement[];
      const tickValues = ticks
        .map((tick) => {
          const transform = tick.getAttribute('transform') || '';
          const match = transform.match(/translate\(\s*([-\d.]+)[ ,]+([-\d.]+)\)/);
          if (match) {
            // match[2] is the Y pixel position
            const yPixel = parseFloat(match[2] ?? '');
            return Number.isFinite(yPixel) ? yScale.invert(yPixel) : null;
          }
          return null;
        })
        .filter((v): v is number => v !== null);
      setYTickValues(tickValues);
    }
  }, [yAxis, yScale]);

  // attach zoom behavior
  const interactionRef = React.useRef<SVGGElement>(null);

  type BrushedInterval = [[number, number], [number, number]] | undefined;
  const [brushedInterval, setBrushedInterval] = React.useState<BrushedInterval>(undefined);
  const handleChangeBrushedInterval = React.useCallback((brushedInterval: BrushedInterval) => {
    if (brushedInterval == null) {
      setBrushedInterval(undefined);
      return;
    }
    setBrushedInterval(brushedInterval);
  }, []);

  // add in brush
  useBrush({
    ref: interactionRef, // <-- must be a <g> for d3-brush
    xScale,
    yScale,
    margin,
    innerWidth,
    height,
    innerHeight,
    intervalBounds: [xExtent as [number, number], yExtent as [number, number]],
    onChangeBrushedInterval: handleChangeBrushedInterval,
    brushedInterval,
    hideBrushOnEnd: false,
  });
  const brushedPoints = React.useMemo<DataPoint[] | undefined>(() => {
    if (brushedInterval == null) return undefined;
    let xMin = brushedInterval[0][0];
    let yMin = brushedInterval[0][1];
    let xMax = brushedInterval[1][0];
    let yMax = brushedInterval[1][1];

    if (xMin > xMax) {
      [xMax, xMin] = [xMin, xMax];
    }
    if (yMin > yMax) {
      [yMax, yMin] = [yMin, yMax];
    }
    const returned_value = data.filter(
      (d) => xMin <= d.x && d.x <= xMax && yMin <= d.y && d.y <= yMax,
    );
    console.log('brushed points: ', returned_value);

    return returned_value;
  }, [brushedInterval, data]);

  // get hover point
  const { points } = useHover({
    ref: interactionRef,
    strategy: 'nearest',
    datasets: React.useMemo(() => [{ dataset: data }], [data]),
    xScale,
    yScale,
    xAccessor: (d: DataPoint) => d.x,
    yAccessor: (d: DataPoint) => d.y,
    radius: 100,
    xOffset: margin.left,
    yOffset: margin.top,
  });

  let hoverPoint: DataPoint | undefined = points?.[0]?.d as DataPoint | undefined;
  //console.log("hoveredPoint(s): ", hoverPoint)
  //console.log("store comparison: ", hoveredDistricts);

  const hoverIsBrushed = hoverPoint !== undefined && brushedPoints?.includes(hoverPoint) === true;

  const desiredHoveredDistricts = React.useMemo<string[]>(() => {
    if (brushedPoints?.length) {
      return brushedPoints.map((d) => d.districtId);
    }
    if (hoverPoint != null) {
      return [hoverPoint.districtId];
    }
    return [];
  }, [brushedPoints, hoverPoint]);

  if (hoveredDistricts.length > 0) {
    for (let i = 0; i < data.length; i++) {
      if (data[i].districtId == hoveredDistricts[0]) {
        hoverPoint = data[i];
      }
    }
  }

  // Apply hover updates as a side-effect (not during render) and guard redundant writes
  React.useEffect(() => {
    if (!arrayEquals(desiredHoveredDistricts, hoveredDistricts) && hoverSource !== 'map') {
      // schedule async to avoid React/Chart re-entrancy
      requestAnimationFrame(() => setHoverFromPlot(desiredHoveredDistricts));
    }
  }, [desiredHoveredDistricts, hoveredDistricts, hoverSource, setHoverFromPlot]);
  return (
    <div style={{ width, height, position: 'relative' }}>
      <svg width={width} height={height}>
        <g transform={`translate(${margin.left} ${margin.top})`}>
          {/* [CO-4] Grid lines aligned with axis tick marks */}
          <g className="grid-lines">
            {/* Vertical grid lines (from x-axis ticks) */}
            {yTickValues.map((tick) => (
              <line
                key={`y-grid-${String(tick)}`}
                x1={0}
                y1={yScale(tick)}
                x2={innerWidth}
                y2={yScale(tick)}
                stroke="#e0e0e0"
                strokeWidth={1}
                strokeDasharray="2,2"
              />
            ))}
            {/* Horizontal grid lines (from y-axis ticks) */}
            {xValues.map((tick) => (
              <line
                key={`x-grid-${String(tick)}`}
                x1={xScale(tick)}
                y1={0}
                x2={xScale(tick)}
                y2={innerHeight}
                stroke="#e0e0e0"
                strokeWidth={1}
                strokeDasharray="2,2"
              />
            ))}
          </g>
          {/* [CO-6] Mean lines for yea, nay, and all of dataset*/}
          <g className="mean-lines">
            {/* Green mean line (y=1 dataset) at x=0 */}
            {/* To change the x position of this mean line, modify the xScale(0) value below */}
            {mean_yea != null && (
              <>
                <line
                  x1={0}
                  y1={yScale(mean_yea)}
                  x2={innerWidth}
                  y2={yScale(mean_yea)}
                  stroke={colors[0]}
                  strokeWidth={1.5}
                  strokeDasharray="4,2"
                />
                {/* label for the yea mean line */}
                <text
                  x={innerWidth + 6}
                  y={yScale(mean_yea) + 2}
                  textAnchor="start"
                  alignmentBaseline="middle"
                  fontSize={11}
                  fontWeight={450}
                  stroke="white"
                  strokeWidth={3}
                  paintOrder="stroke"
                >
                  {`Mean (`}
                  <tspan fill={getColor(1)} stroke="none">
                    Yea
                  </tspan>
                  {`)`}
                </text>
              </>
            )}
            {/* Yellow mean line (y=0 dataset) at x=0.1 */}
            {/* To change the x position of this mean line, modify the xScale(0.1) value below */}
            {mean_nay != null && (
              <>
                <line
                  x1={0}
                  y1={yScale(mean_nay)}
                  x2={innerWidth}
                  y2={yScale(mean_nay)}
                  stroke={colors[1]}
                  strokeWidth={1.5}
                  strokeDasharray="4,2"
                />
                {/* label for the nay mean line */}
                <text
                  x={innerWidth + 6}
                  y={yScale(mean_nay) + 2}
                  textAnchor="start"
                  alignmentBaseline="middle"
                  fontSize={11}
                  fontWeight={450}
                  stroke="white"
                  strokeWidth={3}
                  paintOrder="stroke"
                >
                  {`Mean (`}
                  <tspan fill={getColor(0)} stroke="none">
                    Nay
                  </tspan>
                  {`)`}
                </text>
              </>
            )}
            {/* Grey line for mean of all data */}
            {mean_all != null && (
              <>
                <line
                  x1={0}
                  y1={yScale(mean_all)}
                  x2={innerWidth}
                  y2={yScale(mean_all)}
                  stroke={voteNoDataColor ?? '#000000'}
                  strokeWidth={1.5}
                  strokeDasharray="4,2"
                />
                {/* label for the all mean line */}
                <text
                  x={innerWidth + 6}
                  y={yScale(mean_all) + 2}
                  textAnchor="start"
                  alignmentBaseline="middle"
                  fontSize={11}
                  fontWeight={450}
                  stroke="white"
                  strokeWidth={3}
                  paintOrder="stroke"
                >
                  {`Mean (`}
                  <tspan fill={voteNoDataColor ?? '#000000'} stroke="none">
                    All
                  </tspan>
                  {`)`}
                </text>
              </>
            )}
          </g>
          {/* [CO-2] Y-axis rendered at left edge */}
          <g ref={xAxisRef} transform={`translate(0, ${innerHeight})`} />
          {/* Y-axis label */}
          <text
            transform={`translate(${-margin.left + 15}, ${innerHeight / 2}) rotate(-90)`}
            textAnchor="middle"
            fontWeight={450}
            fontSize={18}
            fill="#111"
            style={{
              shapeRendering: 'crispEdges',
              willChange: 'transform',
            }}
            paintOrder="stroke"
          >
            {metricTitle}
          </text>
          {/* [CO-2] X-axis rendered at bottom of plot area */}
          <g ref={yAxisRef} transform={`translate(0, 0)`} />
          {/* X-axis label */}
          <text
            x={innerWidth / 2}
            y={innerHeight + 40}
            textAnchor="middle"
            fontWeight={450}
            fontSize={18}
            fill="#111"
            style={{
              shapeRendering: 'crispEdges',
              willChange: 'transform',
            }}
            paintOrder="stroke"
          >
            Vote
          </text>
          {data.map((d) => {
            const x = xScale(d.x);
            const y = yScale(d.y);
            const r = pointRadius;
            // [CO-5] Use diamond for y=1 (green points), circle for y=0
            if (d.x === 1) {
              return (
                <path
                  key={d.id}
                  d={getDiamondPath(r)}
                  transform={`translate(${x}, ${y})`}
                  // [CO-1] Use d.y (dataset value) instead of y (pixel coordinate) for color
                  stroke={getColor(d.x)}
                  fill="transparent"
                />
              );
            }
            return (
              <circle
                key={d.id}
                cx={x}
                cy={y}
                r={r}
                // [CO-1] Use d.y (dataset value) instead of y (pixel coordinate) for color
                stroke={getColor(d.x)}
                fill="transparent"
              />
            );
          })}
          {brushedPoints?.map((d) => {
            const x = xScale(d.x);
            const y = yScale(d.y);
            const r = pointRadius;
            // [CO-5] Use diamond for y=0 (green points), circle for y=1
            if (d.y === 0) {
              return (
                <circle
                  key={d.id}
                  cx={x}
                  cy={y}
                  r={r}
                  stroke={'white'}
                  // [CO-1] Use d.y (dataset value) instead of y (pixel coordinate) for color
                  fill={getColor(d.x)}
                />
              );
            }
            return (
              <path
                key={d.id}
                d={getDiamondPath(r)}
                transform={`translate(${x}, ${y})`}
                stroke="white"
                // [CO-1] Use d.y (dataset value) instead of y (pixel coordinate) for color
                fill={getColor(d.x)}
              />
            );
          })}
          {hoverPoint && (
            <g transform={`translate(${xScale(hoverPoint.x)} ${yScale(hoverPoint.y)})`}>
              <text dx={pointRadius * 2 + 4} dy="0.33em" stroke="white" strokeWidth={4}>
                {hoverPoint.id}
              </text>
              <text dx={pointRadius * 2 + 4} dy="0.33em">
                {hoverPoint.id}
              </text>
              {/* [CO-5] Use diamond for y=1 (green points), circle for y=0 */}
              {/* [CO-1] Use hoverPoint.y (dataset value) instead of undefined y variable for color */}
              {hoverPoint.x === 0 ? (
                <circle
                  key={hoverPoint.id}
                  cx={0}
                  cy={0}
                  r={pointRadius * 2}
                  fill={hoverIsBrushed ? getColor(hoverPoint.x) : 'transparent'}
                  stroke="gray"
                  strokeWidth={hoverIsBrushed ? 4 : 2}
                />
              ) : (
                <path
                  key={hoverPoint.id}
                  d={getDiamondPath(pointRadius * 2)}
                  fill={hoverIsBrushed ? getColor(hoverPoint.x) : 'transparent'}
                  stroke="gray"
                  strokeWidth={hoverIsBrushed ? 4 : 2}
                />
              )}
            </g>
          )}
        </g>

        <g ref={interactionRef}>
          <rect x={0} y={0} width={width} height={height} fill={'red'} fillOpacity={0.0} />
        </g>
      </svg>
    </div>
  );
}

//export default ScatterplotWithBrush;
